import { postH5Json } from './h5Api';

export interface PcUser {
  userId: string;
  nickName: string;
  avatar: string;
  accessToken: string;
  expiresAt: number;
  refreshToken?: string;
  refreshExpiresAt?: number;
  preview?: boolean;
}

export interface ArtistApplication {
  artistType: number;
  artistName: string;
  socialLinks: { platform: string; url: string }[];
  socialProfileUrl: string[];
  contactEmail: string;
  introVideoUrl?: string;
}

export interface ArtistApplicationStatus extends ArtistApplication {
  auditStatus: number;
  auditRecordId?: number;
  createTime?: string;
  rejectReason?: string;
}

export interface SongApplication {
  songName: string;
  singerName: string;
  lyricist?: string;
  composer?: string;
  category?: number[];
  language?: string;
  sourceFile?: string;
  accompanyFile?: string;
  lrcFile?: string;
}

export interface SongRecord extends SongApplication {
  auditRecordId: number;
  auditStatus: number;
  submitTime: string;
  rejectReason?: string;
}

export interface SongPage {
  pageNum: number;
  pageSize: number;
  total: number;
  totalCount?: number;
  list: SongRecord[];
}

export interface SongCategory {
  id: number;
  name: string;
}

interface LoginInfo {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
  userId: string | number;
  userInfo: { userId: string | number; nickName: string; avatar: string };
}

interface QrStatus {
  qrStatus: number;
  loginInfo?: LoginInfo;
}

interface RefreshResult {
  accessToken: string;
  accessTokenExpiresIn: number;
  refreshToken: string;
  refreshTokenExpiresIn: number;
  userId: string | number;
}

interface OssSts {
  expiration: string;
  accessKeyId: string;
  accessKeySecret: string;
  securityToken: string;
  bucketName: string;
  directory: string;
  endpoint: string;
}

const LOGIN_KEY = 'koemori-pc-login';
const PREVIEW_ARTIST_KEY = 'koemori-preview-artist';
const PREVIEW_SONGS_KEY = 'koemori-preview-songs';
const TOKEN_BUFFER_MS = 30_000;
export const PC_LOGIN_EXPIRED_EVENT = 'koemori-pc-login-expired';
export const PC_LOGIN_EXPIRED_MESSAGE = 'ログインの有効期限が切れました。QRコードを再度読み取ってログインしてください。';
let expirationNotified = false;
let pendingRefresh: { user: PcUser; promise: Promise<string> } | null = null;

function sameLogin(left: PcUser, right: PcUser) {
  return left.userId === right.userId && left.accessToken === right.accessToken && left.refreshToken === right.refreshToken;
}

function isLoginExpiredError(reason: unknown): boolean {
  return reason instanceof Error && Number((reason as Error & { code?: number }).code) === 100307;
}

export function expirePcLogin(expectedUser?: PcUser) {
  if (expectedUser) {
    // 旧请求失败不能清除用户刚刚重新登录得到的新会话，也不打断主动退出。
    let current: PcUser | null = null;
    try { current = JSON.parse(window.sessionStorage.getItem(LOGIN_KEY) || 'null') as PcUser | null; } catch { /* unavailable storage */ }
    if (!current || !sameLogin(current, expectedUser)) return;
  }
  clearPcUser();
  if (!expirationNotified) {
    expirationNotified = true;
    window.dispatchEvent(new Event(PC_LOGIN_EXPIRED_EVENT));
  }
}

const isPreview = () => import.meta.env.DEV && readPcUser()?.preview === true;

function readPreviewArtist(): ArtistApplicationStatus | null {
  try { return JSON.parse(window.sessionStorage.getItem(PREVIEW_ARTIST_KEY) || 'null') as ArtistApplicationStatus | null; }
  catch { return null; }
}

function readPreviewSongs(): SongRecord[] {
  try {
    const records = JSON.parse(window.sessionStorage.getItem(PREVIEW_SONGS_KEY) || '[]') as unknown;
    return Array.isArray(records) ? records as SongRecord[] : [];
  } catch { return []; }
}

export function createPreviewUser(): PcUser {
  if (!import.meta.env.DEV) throw new Error('プレビュー用ログインは開発環境でのみ利用できます。');
  const user: PcUser = {
    userId: 'PREVIEW-USER', nickName: 'Koemori プレビューアカウント', avatar: '/logo.webp',
    accessToken: 'preview-only', expiresAt: Date.now() + 12 * 60 * 60 * 1000, preview: true,
  };
  window.sessionStorage.setItem(LOGIN_KEY, JSON.stringify(user));
  expirationNotified = false;
  return user;
}

export function readPcUser(): PcUser | null {
  try {
    const raw = window.sessionStorage.getItem(LOGIN_KEY);
    if (!raw) return null;
    const user = JSON.parse(raw) as PcUser;
    const accessValid = Number.isFinite(user.expiresAt) && user.expiresAt > Date.now();
    const refreshValid = Boolean(user.refreshToken && Number.isFinite(user.refreshExpiresAt) && user.refreshExpiresAt! > Date.now());
    if (user.accessToken && user.userId && (accessValid || (!user.preview && refreshValid)) && (!user.preview || import.meta.env.DEV)) return user;
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
  clearPcUser();
  return null;
}

export function clearPcUser() {
  try { window.sessionStorage.removeItem(LOGIN_KEY); } catch { /* ignore */ }
}

export function storePcLogin(login: LoginInfo): PcUser {
  if (!login?.accessToken || !login.userInfo?.userId) throw new Error('ログイン処理に失敗しました。もう一度ログインをお試しください。');
  const user: PcUser = {
    userId: String(login.userInfo.userId || login.userId),
    nickName: login.userInfo.nickName || 'Koemori 用户',
    avatar: login.userInfo.avatar || '/logo.webp',
    accessToken: login.accessToken,
    expiresAt: Date.now() + Number(login.accessTokenExpiresIn || 0) * 1000,
    refreshToken: login.refreshToken || undefined,
    refreshExpiresAt: login.refreshToken ? Date.now() + Number(login.refreshTokenExpiresIn || 0) * 1000 : undefined,
  };
  if (!Number.isFinite(user.expiresAt) || user.expiresAt <= Date.now()) throw new Error('認証情報の取得に失敗しました。再度ログインしてください。');
  if (user.refreshToken && (!Number.isFinite(user.refreshExpiresAt) || user.refreshExpiresAt! <= Date.now())) throw new Error('ログインの有効期限が切れました。再度ログインしてください。');
  try { window.sessionStorage.setItem(LOGIN_KEY, JSON.stringify(user)); } catch { /* memory-only login */ }
  expirationNotified = false;
  return user;
}

async function getValidAccessToken(): Promise<string> {
  const user = readPcUser();
  if (!user) { expirePcLogin(); throw new Error(PC_LOGIN_EXPIRED_MESSAGE); }
  if (user.preview) throw new Error('プレビューアカウントでは実際のAPIを利用できません。');
  if (user.expiresAt > Date.now() + TOKEN_BUFFER_MS) return user.accessToken;
  if (user.expiresAt > Date.now() && !user.refreshToken) return user.accessToken;
  if (!user.refreshToken || !user.refreshExpiresAt || user.refreshExpiresAt <= Date.now()) {
    expirePcLogin(user);
    throw new Error(PC_LOGIN_EXPIRED_MESSAGE);
  }
  if (!pendingRefresh || !sameLogin(pendingRefresh.user, user)) {
    const refresh = { user, promise: null as unknown as Promise<string> };
    refresh.promise = (async () => {
      const result = await postH5Json<RefreshResult>('/h5/auth/token/refresh', { refreshToken: user.refreshToken });
      if (!result?.accessToken || !result.refreshToken || !result.userId ||
          String(result.userId) !== user.userId ||
          !Number.isFinite(Number(result.accessTokenExpiresIn)) || Number(result.accessTokenExpiresIn) <= 0 ||
          !Number.isFinite(Number(result.refreshTokenExpiresIn)) || Number(result.refreshTokenExpiresIn) <= 0) {
        throw new Error('ログインの継続に失敗しました。お手数ですが再度ログインをお願いいたします。');
      }
      const renewed: PcUser = {
        ...user,
        accessToken: result.accessToken,
        expiresAt: Date.now() + Number(result.accessTokenExpiresIn) * 1000,
        refreshToken: result.refreshToken,
        refreshExpiresAt: Date.now() + Number(result.refreshTokenExpiresIn) * 1000,
      };
      const current = readPcUser();
      if (!current || !sameLogin(current, user)) {
        throw new Error('ログイン状態が変更されました。もう一度お試しください。');
      }
      window.sessionStorage.setItem(LOGIN_KEY, JSON.stringify(renewed));
      return renewed.accessToken;
    })().catch((reason: unknown) => {
      if (isLoginExpiredError(reason)) {
        expirePcLogin(user);
        throw new Error(PC_LOGIN_EXPIRED_MESSAGE);
      }
      // 网络异常、限流和服务器错误保留会话，允许稍后重试。
      throw reason;
    }).finally(() => { if (pendingRefresh === refresh) pendingRefresh = null; });
    pendingRefresh = refresh;
  }
  return pendingRefresh.promise;
}

async function postPcJson<T>(uri: string, data: unknown): Promise<T> {
  const token = await getValidAccessToken();
  const user = readPcUser();
  if (!user || user.accessToken !== token) throw new Error('ログイン状態が変更されました。もう一度お試しください。');
  try { return await postH5Json<T>(uri, data, token); }
  catch (reason) {
    if (isLoginExpiredError(reason)) {
      expirePcLogin(user);
      throw new Error(PC_LOGIN_EXPIRED_MESSAGE);
    }
    throw reason;
  }
}

export async function logoutPcUser(): Promise<void> {
  const user = readPcUser();
  try {
    if (user && !user.preview) {
      const token = await getValidAccessToken();
      await postH5Json<unknown>('/h5/auth/token/logout', {}, token);
    }
  } finally {
    clearPcUser();
  }
}

const QR_REQUEST_TIMEOUT_MS = 8_000;

async function withQrRequestTimeout<T>(request: (requestSignal: AbortSignal) => Promise<T>, signal?: AbortSignal): Promise<T> {
  const timeoutSignal = AbortSignal.timeout(QR_REQUEST_TIMEOUT_MS);
  const requestSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
  try {
    return await request(requestSignal);
  } catch (reason) {
    if (timeoutSignal.aborted && !signal?.aborted) {
      throw new Error('QRコードの取得がタイムアウトしました。通信環境をご確認のうえ、再度お試しください。');
    }
    throw reason;
  }
}

export async function requestLoginQr(signal?: AbortSignal): Promise<string> {
  const result = await withQrRequestTimeout(
    (requestSignal) => postH5Json<{ qrcode: string }>('/h5/login/qrcode', {}, undefined, requestSignal),
    signal,
  );
  if (!result?.qrcode) throw new Error('QRコードを表示できませんでした。再読み込みをお試しください。');
  return result.qrcode;
}

export async function queryLoginQr(qrcode: string, signal?: AbortSignal): Promise<QrStatus> {
  const result = await withQrRequestTimeout(
    (requestSignal) => postH5Json<QrStatus>('/h5/login/qr-status', { qrcode }, undefined, requestSignal),
    signal,
  );
  if (![1, 2, 3].includes(Number(result?.qrStatus))) throw new Error('ログイン状態の確認に失敗しました。もう一度QRコードをスキャンしてください。');
  return result;
}

export async function submitArtistApplication(data: ArtistApplication): Promise<unknown> {
  if (isPreview()) {
    const record: ArtistApplicationStatus = {
      ...data, auditStatus: 1, auditRecordId: Date.now(), createTime: new Date().toLocaleString('zh-CN'),
    };
    window.sessionStorage.setItem(PREVIEW_ARTIST_KEY, JSON.stringify(record));
    return { auditRecordId: record.auditRecordId };
  }
  return postPcJson<unknown>('/h5/artist/apply/submit', data);
}

export async function queryArtistApplication(): Promise<ArtistApplicationStatus | null> {
  if (isPreview()) return readPreviewArtist();
  return postPcJson<ArtistApplicationStatus | null>('/h5/artist/apply/query', {});
}

export async function requireApprovedBand(): Promise<ArtistApplicationStatus> {
  const application = await queryArtistApplication();
  if (!application || application.auditStatus === 4) throw new Error('先にアーティスト登録を申請し、承認後に楽曲をアップロードしてください。');
  if (application.auditStatus === 1) throw new Error('アーティスト登録の審査中です。承認後に楽曲をアップロードできます。');
  if (application.auditStatus === 3) throw new Error('アーティスト登録が承認されませんでした。内容を修正して再申請してください。');
  if (application.auditStatus !== 2) throw new Error('アーティスト審査状況を確認できません。しばらくしてから再度お試しください。');
  return application;
}

export function approvePreviewBandApplication(): ArtistApplicationStatus {
  if (!isPreview()) throw new Error('審査シミュレーションは開発環境のプレビューアカウントでのみ利用できます。');
  const application = readPreviewArtist();
  if (!application || application.auditStatus !== 1) {
    throw new Error('先に審査待ちのアーティスト登録申請を送信してください。');
  }
  const approved = { ...application, auditStatus: 2 };
  window.sessionStorage.setItem(PREVIEW_ARTIST_KEY, JSON.stringify(approved));
  return approved;
}

export async function cancelArtistApplication(): Promise<unknown> {
  if (isPreview()) {
    const record = readPreviewArtist();
    if (!record) throw new Error('取り消せる申請がありません。');
    window.sessionStorage.setItem(PREVIEW_ARTIST_KEY, JSON.stringify({ ...record, auditStatus: 4 }));
    return;
  }
  return postPcJson<unknown>('/h5/artist/apply/cancel', {});
}

export async function submitSong(data: SongApplication): Promise<unknown> {
  await requireApprovedBand();
  if (isPreview()) {
    const record: SongRecord = {
      ...data, auditRecordId: Date.now(), auditStatus: 1, submitTime: new Date().toLocaleString('zh-CN'),
    };
    window.sessionStorage.setItem(PREVIEW_SONGS_KEY, JSON.stringify([record, ...readPreviewSongs()]));
    return { auditRecordId: record.auditRecordId };
  }
  return postPcJson<unknown>('/h5/artist/song/submit', data);
}

export async function listSongCategories(): Promise<SongCategory[]> {
  if (isPreview()) return [{ id: 1, name: 'J-POP' }, { id: 2, name: 'K-POP' }, { id: 3, name: 'ANISONG' }];
  const result = await postPcJson<unknown>('/h5/artist/song/category', {});
  const rows = Array.isArray(result) ? result : (result && typeof result === 'object' && Array.isArray((result as { rows?: unknown[] }).rows) ? (result as { rows: unknown[] }).rows : []);
  return rows.map((item) => {
    const value = item as { id?: unknown; categoryId?: unknown; name?: unknown; categoryName?: unknown };
    return { id: Number(value.id ?? value.categoryId), name: String(value.name ?? value.categoryName ?? '') };
  }).filter((item) => Number.isInteger(item.id) && item.id > 0 && item.name);
}

export async function listSongs(pageNum = 1, pageSize = 10, auditStatus?: number): Promise<SongPage> {
  if (isPreview()) {
    const records = readPreviewSongs().filter((item) => !auditStatus || item.auditStatus === auditStatus);
    return { pageNum, pageSize, total: records.length, list: records.slice((pageNum - 1) * pageSize, pageNum * pageSize) };
  }
  const raw = await postPcJson<unknown>('/h5/artist/song/list', {
    pageNum, pageSize, ...(auditStatus ? { auditStatus } : {}),
  });
  const value = raw && typeof raw === 'object' ? raw as { pageNum?: unknown; pageSize?: unknown; total?: unknown; totalCount?: unknown; rows?: unknown; list?: unknown } : {};
  const rows = Array.isArray(value.list) ? value.list : Array.isArray(value.rows) ? value.rows : [];
  // Some deployments return the status as a string (or as `status`). Normalize
  // it here so the UI filters and labels use the documented auditStatus field.
  const list = rows.map((item) => {
    const record = item as SongRecord & { status?: unknown };
    return {
      ...record,
      auditStatus: Number(record.auditStatus ?? record.status),
    } as SongRecord;
  });
  const total = Number(value.totalCount ?? value.total ?? list.length) || 0;
  return { pageNum: Number(value.pageNum) || pageNum, pageSize: Number(value.pageSize) || pageSize, total, totalCount: total, list };
}

// The supplied document lists /list for details too; keep that documented URL
// until backend supplies a distinct detail path, and validate its shape.
export async function getSongDetail(auditRecordId: number): Promise<SongRecord> {
  if (isPreview()) {
    const record = readPreviewSongs().find((item) => item.auditRecordId === auditRecordId);
    if (!record) throw new Error('楽曲が見つかりません。');
    return record;
  }
  const detail = await postPcJson<SongRecord>('/h5/artist/song/detail', { auditRecordId });
  if (!detail || Number(detail.auditRecordId) !== auditRecordId) throw new Error('楽曲情報の取得に失敗しました。時間をおいて再度お試しください。');
  return detail;
}

function verifyImageReadable(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timeout = window.setTimeout(() => {
      image.onload = null;
      image.onerror = null;
      reject(new Error('画像の処理に時間がかかっています。時間をおいて再度お試しください。'));
    }, 15_000);
    image.onload = () => { window.clearTimeout(timeout); resolve(); };
    image.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('画像を読み込めませんでした。通信環境をご確認のうえ、再度お試しください。'));
    };
    image.src = url;
  });
}

function uploadFileExtension(file: File, sceneCode: 'artist' | 'song', verifyImage: boolean): string {
  if (verifyImage) {
    const imageTypes: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };
    const extension = imageTypes[file.type];
    if (!extension || file.size === 0 || file.size > 5 * 1024 * 1024) {
      throw new Error('画像はPNG/JPG/WebPのみ、5MB以下に対応しています。');
    }
    return extension;
  }
  const extension = file.name.match(/\.([a-z0-9]+)$/i)?.[1].toLowerCase();
  const allowed = sceneCode === 'artist' ? ['mp4', 'mov'] : ['wav', 'mp3', 'm4a', 'txt', 'lrc', 'pdf'];
  if (!extension || !allowed.includes(extension)) throw new Error('対応していないファイル形式です。選択したファイルを確認してください。');
  const limit = sceneCode === 'artist' ? 100 * 1024 * 1024 : ['txt', 'lrc', 'pdf'].includes(extension) ? 1024 * 1024 : 50 * 1024 * 1024;
  if (file.size === 0 || file.size > limit) throw new Error(`ファイルは空にできません。${Math.round(limit / 1024 / 1024)}MB以下にしてください。`);
  return extension;
}

export async function uploadPcFile(file: File, sceneCode: 'artist' | 'song', verifyImage = false): Promise<string> {
  const randomParts = crypto.getRandomValues(new Uint32Array(2));
  const numericName = `${Date.now()}${Array.from(randomParts, (part) => String(part).padStart(10, '0')).join('')}`;
  const filename = `${numericName}.${uploadFileExtension(file, sceneCode, verifyImage)}`;
  if (isPreview()) return `preview/${sceneCode}/${filename}`;
  let sts: OssSts;
  try {
    sts = await postPcJson<OssSts>('/h5/artist/oss/sts', { sceneCode });
  } catch (reason) {
    throw new Error(`获取 OSS 上传凭证失败：${reason instanceof Error ? reason.message : '请稍后重试。'}`);
  }
  if (!sts?.accessKeyId || !sts.accessKeySecret || !sts.securityToken || !sts.bucketName || !sts.directory || !sts.endpoint) {
    throw new Error('OSS 临时凭证缺少必要字段。');
  }
  const expiration = Date.parse(sts.expiration);
  if (!Number.isFinite(expiration) || expiration <= Date.now() + 30_000) throw new Error('OSS 临时凭证已过期或缺少有效期，请重试。');
  const { default: OSS } = await import('ali-oss');
  const endpoint = sts.endpoint.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const region = endpoint.match(/^(oss-[a-z0-9-]+)\.aliyuncs\.com$/)?.[1];
  if (!region) throw new Error('OSS endpoint 无法推导区域，请后端提供标准区域地址。');
  const client = new OSS({
    region, endpoint, bucket: sts.bucketName, authorizationV4: true,
    accessKeyId: sts.accessKeyId, accessKeySecret: sts.accessKeySecret,
    stsToken: sts.securityToken, secure: true, timeout: 120000,
  });
  // The API normally returns a key prefix (for example `song/123`), but some
  // environments return the full bucket URL. Strip the URL part before using
  // it as an OSS object key; otherwise the submitted URL becomes
  // `https://bucket/https%3A//bucket/...`.
  let directory = sts.directory.trim();
  if (/^https?:\/\//i.test(directory)) {
    try { directory = new URL(directory).pathname; } catch { /* keep the raw value */ }
  }
  const key = `${directory.replace(/^\/+|\/+$/g, '')}/${filename}`;
  const uploadFile = new File([file], filename, { type: file.type, lastModified: file.lastModified });
  try {
    const date = new Date();
    const ossDate = date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const credential = `${sts.accessKeyId}/${ossDate.slice(0, 8)}/${region.replace(/^oss-/, '')}/oss/aliyun_v4_request`;
    const policy = {
      expiration: new Date(Math.min(expiration - 5000, Date.now() + 10 * 60_000)).toISOString(),
      conditions: [
        { bucket: sts.bucketName },
        { key },
        { 'x-oss-credential': credential },
        { 'x-oss-date': ossDate },
        { 'x-oss-signature-version': 'OSS4-HMAC-SHA256' },
        { 'x-oss-security-token': sts.securityToken },
        ['content-length-range', 1, file.size + 64 * 1024],
        ['eq', '$success_action_status', '200'],
      ],
    };
    const signer = client as typeof client & { signPostObjectPolicyV4: (value: object, at: Date) => string };
    const form = new FormData();
    form.append('key', key);
    form.append('x-oss-date', ossDate);
    form.append('x-oss-credential', credential);
    form.append('x-oss-signature-version', 'OSS4-HMAC-SHA256');
    form.append('x-oss-security-token', sts.securityToken);
    const policyBytes = new TextEncoder().encode(JSON.stringify(policy));
    form.append('policy', window.btoa(Array.from(policyBytes, (byte) => String.fromCharCode(byte)).join('')));
    form.append('x-oss-signature', signer.signPostObjectPolicyV4(policy, date));
    form.append('success_action_status', '200');
    form.append('file', uploadFile, filename);
    // OSS 缺少跨域响应头时，no-cors 仍能发送表单，但浏览器无法读取成功/失败状态。
    const response = await fetch(`https://${sts.bucketName}.${endpoint}/`, { method: 'POST', mode: 'no-cors', body: form });
    if (response.type !== 'opaque' && !response.ok) {
      const errorBody = await response.text();
      const ossCode = errorBody.match(/<Code>([^<]+)<\/Code>/)?.[1];
      throw new Error(`HTTP ${response.status}${ossCode ? `（${ossCode}）` : ''}`);
    }
  } catch (reason) {
    throw new Error(`上传文件到 OSS 失败：${reason instanceof Error ? reason.message : '请检查网络、OSS 权限或跨域配置。'}`);
  }
  if (verifyImage) {
    try {
      const signedUrl = await client.signatureUrlV4('GET', 60, {}, key);
      await verifyImageReadable(signedUrl);
    } catch (reason) {
      throw new Error(`${reason instanceof Error ? reason.message : '画像の読み込みに失敗しました。'} ファイル名：${key}`);
    }
  }
  // The song submit API expects the OSS object key. It signs this key on the
  // server when returning song records; sending a full URL here would make
  // the server sign `https://bucket/https://bucket/...`.
  return key;
}
